import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Shield, 
  User, 
  Calculator,
  Search,
  Edit2,
  Trash2,
  Key,
  X,
  Check,
  AlertTriangle,
  Clock,
  Activity,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import NotificationSettingsCard from '../components/NotificationSettingsCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: 'admin', label: 'Administrateur', icon: Shield, color: 'text-red-600 bg-red-100' },
  { value: 'comptable', label: 'Comptable', icon: Calculator, color: 'text-blue-600 bg-blue-100' },
  { value: 'employe', label: 'Employé', icon: User, color: 'text-slate-600 bg-slate-100' }
];

const AdminUsersPage = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [healthCheck, setHealthCheck] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nom: '',
    role: 'employe'
  });

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadHealthCheck();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const loadHealthCheck = async () => {
    try {
      const res = await axios.get(`${API}/admin/health-check`);
      setHealthCheck(res.data);
    } catch (e) {
      console.error('Health check failed:', e);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 12) {
      toast.error('Le mot de passe doit contenir au moins 12 caractères');
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, formData);
      toast.success('Utilisateur créé. Il devra changer son mot de passe à la première connexion.');
      setShowModal(false);
      resetForm();
      loadUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = 'Erreur lors de la création';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail.length) msg = detail[0]?.msg || detail[0]?.message || msg;
      toast.error(msg);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        nom: formData.nom,
        role: formData.role
      });
      toast.success('Utilisateur mis à jour');
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleToggleActive = async (user) => {
    const action = user.is_active ? 'désactiver' : 'réactiver';
    if (!window.confirm(`Voulez-vous ${action} l'utilisateur "${user.nom}" ?`)) return;

    try {
      await axios.put(`${API}/admin/users/${user.id}`, {
        is_active: !user.is_active
      });
      toast.success(`Utilisateur ${user.is_active ? 'désactivé' : 'réactivé'}`);
      loadUsers();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Réinitialiser le mot de passe de "${user.nom}" ?\n\nUn mot de passe temporaire sera généré.`)) return;

    try {
      const res = await axios.post(`${API}/admin/users/${user.id}/reset-password`);
      toast.success(
        <div>
          <p>Mot de passe réinitialisé</p>
          <p className="font-mono mt-1 p-2 bg-slate-100 rounded">{res.data.temporary_password}</p>
          <p className="text-xs text-slate-500 mt-1">Communiquez ce mot de passe à l'utilisateur</p>
        </div>,
        { duration: 10000 }
      );
      loadUsers();
    } catch (e) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  // Admin defines a specific password for a user
  const [setPasswordUser, setSetPasswordUser] = useState(null);
  const [setPasswordValue, setSetPasswordValue] = useState('');
  const [setPasswordShow, setSetPasswordShow] = useState(false);
  const [setPasswordForceChange, setSetPasswordForceChange] = useState(false);
  const [setPasswordSaving, setSetPasswordSaving] = useState(false);

  const openSetPassword = (user) => {
    setSetPasswordUser(user);
    setSetPasswordValue('');
    setSetPasswordShow(false);
    setSetPasswordForceChange(false);
  };

  const submitSetPassword = async () => {
    if (setPasswordValue.length < 12) {
      toast.error('Le mot de passe doit contenir au moins 12 caractères');
      return;
    }
    setSetPasswordSaving(true);
    try {
      await axios.post(`${API}/admin/users/${setPasswordUser.id}/set-password`, {
        new_password: setPasswordValue,
        must_change_on_login: setPasswordForceChange,
      });
      toast.success(`Mot de passe de ${setPasswordUser.nom} mis à jour`);
      setSetPasswordUser(null);
      setSetPasswordValue('');
      loadUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = 'Erreur lors de la mise à jour';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail.length) msg = detail[0]?.msg || msg;
      toast.error(msg);
    } finally {
      setSetPasswordSaving(false);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      nom: user.nom,
      role: user.role
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      nom: '',
      role: 'employe'
    });
  };

  const getRoleInfo = (role) => {
    return ROLES.find(r => r.value === role) || ROLES[2];
  };

  const filteredUsers = users.filter(u =>
    u.nom.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Shield className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Accès refusé</h2>
        <p className="text-slate-500 mt-2">Cette page est réservée aux administrateurs.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-users-page">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestion des utilisateurs</h1>
          <p className="text-slate-500 mt-1">Administration des comptes et des permissions</p>
        </div>
        <button
          onClick={() => { resetForm(); setEditingUser(null); setShowModal(true); }}
          className="flex items-center px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvel utilisateur
        </button>
      </div>

      <NotificationSettingsCard />

      {/* Health Check Summary */}
      {healthCheck && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Utilisateurs actifs</span>
              <Users className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-2">{healthCheck.users?.active || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Administrateurs</span>
              <Shield className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600 mt-2">{healthCheck.users?.by_role?.admin || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-amber-700">MDP à changer</span>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-2xl font-bold text-amber-600 mt-2">{healthCheck.users?.pending_password_change || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Événements 24h</span>
              <Activity className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-2">{healthCheck.security_events_24h || 0}</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-96 h-10 pl-10 pr-4 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          />
        </div>
      </div>

      {/* Users list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Utilisateur</th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Rôle</th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Statut</th>
              <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Dernière connexion</th>
              <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUsers.map((user) => {
              const roleInfo = getRoleInfo(user.role);
              const RoleIcon = roleInfo.icon;
              
              return (
                <tr key={user.id} className={`hover:bg-slate-50 ${!user.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleInfo.color}`}>
                        <RoleIcon className="w-5 h-5" />
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-slate-900">{user.nom}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleInfo.color}`}>
                      {roleInfo.label}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {!user.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Désactivé
                      </span>
                    ) : user.must_change_password ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        MDP à changer
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Check className="w-3 h-3 mr-1" />
                        Actif
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {user.last_login ? (
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {new Date(user.last_login).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    ) : (
                      <span className="text-slate-400">Jamais</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Modifier"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openSetPassword(user)}
                        className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg"
                        title="Définir le mot de passe"
                        data-testid={`set-password-btn-${user.id}`}
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={`p-2 rounded-lg ${
                            user.is_active 
                              ? 'text-slate-400 hover:text-red-600 hover:bg-red-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={user.is_active ? 'Désactiver' : 'Réactiver'}
                        >
                          {user.is_active ? <Trash2 className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h2>
              <button 
                onClick={() => { setShowModal(false); setEditingUser(null); }}
                className="p-1 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nom complet *
                </label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900"
                  required
                />
              </div>

              {!editingUser && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Mot de passe temporaire *
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900"
                      required
                      minLength={12}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Minimum 12 caractères. L'utilisateur devra le changer à la première connexion.
                    </p>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rôle *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-900"
                >
                  {ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Role description */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                {formData.role === 'admin' && (
                  <p className="text-slate-600">
                    <Shield className="w-4 h-4 inline mr-1 text-red-500" />
                    Accès total à toutes les fonctionnalités et données.
                  </p>
                )}
                {formData.role === 'comptable' && (
                  <p className="text-slate-600">
                    <Calculator className="w-4 h-4 inline mr-1 text-blue-500" />
                    Lecture seule + accès aux données financières et exports.
                  </p>
                )}
                {formData.role === 'employe' && (
                  <p className="text-slate-600">
                    <User className="w-4 h-4 inline mr-1 text-slate-500" />
                    Accès limité aux projets et devis (lecture et modification).
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingUser(null); }}
                  className="flex-1 h-10 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 h-10 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800"
                >
                  {editingUser ? 'Enregistrer' : 'Créer l\'utilisateur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set Password Modal (admin defines a specific password) */}
      {setPasswordUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSetPasswordUser(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="set-password-modal">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Key className="w-5 h-5 text-emerald-600" /> Définir le mot de passe
                </h3>
                <p className="text-xs text-slate-500 mt-1">{setPasswordUser.nom} — {setPasswordUser.email}</p>
              </div>
              <button onClick={() => setSetPasswordUser(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <strong>Note de sécurité :</strong> les mots de passe existants sont chiffrés et ne peuvent pas être affichés en clair (standard bcrypt). Vous pouvez par contre définir un nouveau mot de passe à tout moment.
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nouveau mot de passe *</label>
                <div className="relative">
                  <input
                    type={setPasswordShow ? 'text' : 'password'}
                    value={setPasswordValue}
                    onChange={(e) => setSetPasswordValue(e.target.value)}
                    className="w-full h-10 px-3 pr-20 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Min 12 caractères"
                    data-testid="set-password-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setSetPasswordShow(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-slate-600 hover:text-slate-900"
                  >
                    {setPasswordShow ? 'Masquer' : 'Afficher'}
                  </button>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-slate-500">{setPasswordValue.length} / 12 caractères minimum</p>
                  <button
                    type="button"
                    onClick={() => {
                      // Generate a strong random password (14 chars)
                      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
                      let p = '';
                      for (let i = 0; i < 14; i++) p += chars[Math.floor(Math.random() * chars.length)];
                      setSetPasswordValue(p);
                      setSetPasswordShow(true);
                    }}
                    className="text-xs text-emerald-600 hover:text-emerald-700"
                  >
                    Générer aléatoire
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={setPasswordForceChange}
                  onChange={(e) => setSetPasswordForceChange(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                />
                Forcer le changement à la prochaine connexion
              </label>
              {setPasswordValue.length >= 12 && (
                <button
                  type="button"
                  onClick={async () => {
                    const origin = window.location.origin;
                    const text = `Voici vos identifiants MAAG :\n\nEmail : ${setPasswordUser.email}\nMot de passe : ${setPasswordValue}\nLien : ${origin}/login`;
                    try {
                      await navigator.clipboard.writeText(text);
                      toast.success('Email + mdp + lien copiés dans le presse-papier');
                    } catch (err) {
                      // Fallback for non-https or old browsers
                      const ta = document.createElement('textarea');
                      ta.value = text;
                      document.body.appendChild(ta);
                      ta.select();
                      try { document.execCommand('copy'); toast.success('Copié'); } catch (e) { toast.error('Copie impossible'); }
                      document.body.removeChild(ta);
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-sky-50 border border-sky-200 text-sky-700 rounded-lg text-sm font-medium hover:bg-sky-100"
                  data-testid="copy-credentials-btn"
                >
                  📋 Copier les identifiants (email + mdp + lien)
                </button>
              )}
            </div>
            <div className="p-5 border-t border-slate-200 bg-slate-50 rounded-b-xl flex items-center justify-end gap-2">
              <button
                onClick={() => setSetPasswordUser(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
              >
                Annuler
              </button>
              <button
                onClick={submitSetPassword}
                disabled={setPasswordSaving || setPasswordValue.length < 12}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-lg text-sm font-semibold"
                data-testid="set-password-submit"
              >
                {setPasswordSaving ? 'Enregistrement...' : 'Définir le mot de passe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
